import { z } from 'zod';
import { NotificationPreferencesSchema } from '../app-notification';
import { MKT_BusinessSettingsSchema } from '../marketplace';
import { BusinessAccessPolicyModeV2, BusinessAccessPolicyModeV2Schema, BusinessActionEnumSchema, BusinessPaymentMethodV2Schema, BusinessProductAreaV2Schema, } from './shared/access-enums-v2';
export var RestaurantPosDefaultTabV2;
(function (RestaurantPosDefaultTabV2) {
    RestaurantPosDefaultTabV2["CUSTOM"] = "CUSTOM";
    RestaurantPosDefaultTabV2["FAVORITES"] = "FAVORITES";
    RestaurantPosDefaultTabV2["ALL"] = "ALL";
})(RestaurantPosDefaultTabV2 || (RestaurantPosDefaultTabV2 = {}));
export var MobilePosDefaultTabV2;
(function (MobilePosDefaultTabV2) {
    MobilePosDefaultTabV2["CUSTOM"] = "CUSTOM";
    MobilePosDefaultTabV2["ACCESSORIES"] = "ACCESSORIES";
    MobilePosDefaultTabV2["DEVICES"] = "DEVICES";
})(MobilePosDefaultTabV2 || (MobilePosDefaultTabV2 = {}));
export var QuickBooksSyncTypeV2;
(function (QuickBooksSyncTypeV2) {
    QuickBooksSyncTypeV2["NIGHTLY"] = "NIGHTLY";
    QuickBooksSyncTypeV2["PER_TRANSACTION"] = "PER_TRANSACTION";
})(QuickBooksSyncTypeV2 || (QuickBooksSyncTypeV2 = {}));
export var ReportingCutoffRuleV2;
(function (ReportingCutoffRuleV2) {
    ReportingCutoffRuleV2["NEXT_DAY_AFTER_CUTOFF"] = "NEXT_DAY_AFTER_CUTOFF";
})(ReportingCutoffRuleV2 || (ReportingCutoffRuleV2 = {}));
/**
 * Verbosity of device diagnostic logging, ordered most to least severe. A
 * device captures records at the configured level and everything above it.
 */
export var DiagnosticsLogLevelV2;
(function (DiagnosticsLogLevelV2) {
    DiagnosticsLogLevelV2["ERROR"] = "ERROR";
    DiagnosticsLogLevelV2["WARN"] = "WARN";
    DiagnosticsLogLevelV2["INFO"] = "INFO";
    DiagnosticsLogLevelV2["DEBUG"] = "DEBUG";
    DiagnosticsLogLevelV2["TRACE"] = "TRACE";
})(DiagnosticsLogLevelV2 || (DiagnosticsLogLevelV2 = {}));
export const RestaurantPosDefaultTabV2Schema = z.nativeEnum(RestaurantPosDefaultTabV2);
export const MobilePosDefaultTabV2Schema = z.nativeEnum(MobilePosDefaultTabV2);
export const QuickBooksSyncTypeV2Schema = z.nativeEnum(QuickBooksSyncTypeV2);
export const ReportingCutoffRuleV2Schema = z.nativeEnum(ReportingCutoffRuleV2);
export const DiagnosticsLogLevelV2Schema = z.nativeEnum(DiagnosticsLogLevelV2);
export const BusinessAccessPolicyV2Schema = z.object({
    mode: BusinessAccessPolicyModeV2Schema.default(BusinessAccessPolicyModeV2.HYBRID),
    /**
     * When enabled, POS sensitive-action confirmations require a fresh PIN for
     * marked actions in every policy mode, without replacing the active operator.
     * Other policy consumers retain their existing role/session evaluation.
     */
    requireUserForSensitiveActions: z.boolean().default(true),
    sensitiveActions: z.array(BusinessActionEnumSchema).default([]),
    /**
     * Role used for an unattended device/session before a user identifies
     * themselves. The aggregate schema validates that this role exists.
     */
    fallbackRoleId: z.string().min(1).optional(),
});
export const LinkedPrintingDeviceV2Schema = z.object({
    sourceDeviceId: z.string().min(1),
    destinationDeviceId: z.string().min(1),
});
export var AutomaticPrintModeV2;
(function (AutomaticPrintModeV2) {
    AutomaticPrintModeV2["NONE"] = "NONE";
    AutomaticPrintModeV2["RECEIPT"] = "RECEIPT";
    AutomaticPrintModeV2["ORDER"] = "ORDER";
    AutomaticPrintModeV2["RECEIPT_AND_ORDER"] = "RECEIPT_AND_ORDER";
})(AutomaticPrintModeV2 || (AutomaticPrintModeV2 = {}));
export const AutomaticPrintModeV2Schema = z.nativeEnum(AutomaticPrintModeV2);
/** Existing paid-sale behavior when a business has not configured a delay. */
export const DEFAULT_PAID_SALE_AUTO_CLOSE_SECONDS = 10;
/**
 * Operational preferences are deliberately kept outside access grants. A
 * setting may enable behavior, but it never authorizes a user by itself.
 */
export const BusinessSettingsV2Schema = z.object({
    ui: z
        .object({
        displayBiggerText: z.boolean().default(false),
    })
        .optional(),
    checkout: z
        .object({
        allowSplitPayments: z.boolean().default(false),
        defaultPaymentMethod: BusinessPaymentMethodV2Schema.optional(),
        customerPaysFeesDefault: z.boolean().optional(), // user changeable
        taxesEnabledDefault: z.boolean().optional(), // user changeable
        tipSelectionEnabled: z.boolean().default(false), // user changeable
        defaultTerminalToManualPayments: z.boolean().default(false),
        restaurantPosDefaultViewTab: RestaurantPosDefaultTabV2Schema.optional(),
        mobilePosDefaultViewTab: MobilePosDefaultTabV2Schema.optional(),
        displayLineItemsOnTerminal: z.boolean().optional(), // user changeable
        holdSalesEnabled: z.boolean().optional(), // user changeable
    })
        .optional(),
    payments: z
        .object({
        topUpsEnabled: z.boolean().default(false),
        /** Admin-only: email active owners after a successful V4 refund. */
        notifyOwnersOnRefund: z.boolean().default(false),
        /** Automatically leave the successful-payment screen after a delay. */
        paidSaleAutoCloseEnabled: z.boolean().default(true),
        /** Whole seconds before an untouched successful-payment screen closes. */
        paidSaleAutoCloseSeconds: z
            .number()
            .int()
            .positive()
            .default(DEFAULT_PAID_SALE_AUTO_CLOSE_SECONDS),
    })
        .optional(),
    system: z
        .object({
        offlineModeEnabled: z.boolean().default(false),
        timeZone: z.string().min(1),
        lockoutTimeMinutes: z.number().int().nonnegative().optional(),
        // Require an operator to authenticate with their PIN before accessing the system.
        requireOperatorAuthentication: z.boolean().default(false),
        // Sliding inactivity timeout. Zero keeps the current operator authenticated
        // until an explicit/security invalidation ends the session.
        operatorAuthenticationDurationMinutes: z
            .number()
            .int()
            .nonnegative()
            .default(15),
    })
        .optional(), // user changeable
    /**
     * Device diagnostic logging. Devices always keep a light local log; this
     * section turns on verbose capture plus background upload to PointCraft.
     * Admin-only: deliberately absent from
     * {@link BusinessEditableSettingsV2Schema}.
     */
    diagnostics: z
        .object({
        /** Diagnostic mode: verbose capture plus background upload. */
        enabled: z.boolean().default(false),
        level: DiagnosticsLogLevelV2Schema.default(DiagnosticsLogLevelV2.INFO),
        /**
         * ISO datetime after which devices revert to light local logging on
         * their own. Absent means the mode runs until it is turned off.
         */
        enabledUntil: z.string().datetime().optional(),
        /**
         * Business device ids to target. An empty list means every device in
         * the business.
         */
        deviceIds: z.array(z.string().uuid()).max(200).default([]),
    })
        .optional(),
    orders: z
        .object({
        enabled: z.boolean().default(false),
        /** Send eligible Restaurant POS sales after verified full payment approval. */
        autoSendToKitchenAfterPayment: z.boolean().default(false),
        /** KDS prints each incoming item revision once across business devices. */
        kdsAutoPrintOrders: z.boolean().default(false),
        /** Allow the cart-add chime for new KDS tickets, subject to notification access. */
        kdsNewOrderSoundEnabled: z.boolean().default(true),
        autoAcceptOrders: z.boolean().default(false),
        autoCompleteOrders: z.boolean().default(false),
        tableServiceEnabled: z.boolean().default(false),
        holdOrdersEnabled: z.boolean().default(false),
        /**
         * Restaurant POS: offer the Employee (staff meal) order type. A staff meal
         * reaches the kitchen at no charge with no sale (ordersHub
         * `fulfillmentType: 'STAFF_MEAL'`, `paymentState: 'NO_CHARGE'`). Keep off
         * until every POS/KDS device runs a build that reads staff-meal orders:
         * older builds reject the unknown values and lose their order listener.
         */
        staffMealsEnabled: z.boolean().default(false),
        /**
         * First number used when generating sequential order numbers.
         * For example, setting this to 1000 means the next initialized
         * order-number sequence begins at 1000.
         *
         * Changing this setting should not automatically renumber
         * existing orders.
         */
        orderNumberStart: z.number().int().min(0).default(1),
        /**
         * Display's this button in the right panel next to the held sales button.
         * Use for displaying the button that changes the current order to either be Take Out or Dine IN
         */
        displayDineInTakeOutButton: z.boolean().default(false),
    })
        .optional(), // user changeable
    printing: z
        .object({
        enabled: z.boolean().default(false),
        autoPrintReceipt: z.boolean().default(false),
        printDoubleKdsReceipt: z.boolean().default(false),
        automaticPrintMode: AutomaticPrintModeV2Schema.default(AutomaticPrintModeV2.NONE),
        footerText: z.string().optional(),
        linkedPrintingDevices: z.array(LinkedPrintingDeviceV2Schema).default([]),
    })
        .optional(), // user changeable
    reporting: z
        .object({
        cutoffTimeUtc: z.string().min(1).optional(),
        cutoffRule: ReportingCutoffRuleV2Schema.default(ReportingCutoffRuleV2.NEXT_DAY_AFTER_CUTOFF),
        simpleReportsEnabled: z.boolean().default(false),
    })
        .optional(),
    integrations: z
        .object({
        manualTransactionSyncEnabled: z.boolean().default(false),
        quickBooks: z
            .object({
            enabled: z.boolean().default(false),
            syncType: QuickBooksSyncTypeV2Schema.default(QuickBooksSyncTypeV2.NIGHTLY),
            includeCashTransactions: z.boolean().default(false),
        })
            .optional(),
    })
        .optional(),
    notifications: NotificationPreferencesSchema.optional(),
    marketplace: MKT_BusinessSettingsSchema.optional(), // user changeable
    catalog: z
        .object({
        itemCategories: z.array(z.string().min(1)).max(50).default([]),
    })
        .optional(), // user changeable
});
/**
 * Subset of {@link BusinessSettingsV2Schema} that non-admin users are allowed
 * to edit themselves. Keep in sync with the `// user changeable` markers above.
 */
export const BusinessEditableSettingsV2Schema = BusinessSettingsV2Schema.pick({
    orders: true,
    printing: true,
    marketplace: true,
    catalog: true,
}).extend({
    checkout: z
        .object({
        customerPaysFeesDefault: z.boolean().optional(),
        taxesEnabledDefault: z.boolean().optional(),
        tipSelectionEnabled: z.boolean().optional(),
        displayLineItemsOnTerminal: z.boolean().optional(),
        defaultTerminalToManualPayments: z.boolean().default(false),
        restaurantPosDefaultViewTab: RestaurantPosDefaultTabV2Schema.optional(),
        mobilePosDefaultViewTab: MobilePosDefaultTabV2Schema.optional(),
        holdSalesEnabled: z.boolean().optional(), // user changeable
    })
        .optional(),
    system: z
        .object({
        lockoutTimeMinutes: z.number().int().nonnegative().optional(),
    })
        .optional(),
});
/**
 * A freeform label associated with a business (e.g. `NEXT_DAY_PAYOUT`).
 * Uppercase snake_case, trimmed, and capped in length so tags stay usable
 * as search/filter keys.
 */
export const BusinessAccessTagV2Schema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Tag must be uppercase snake_case (e.g. NEXT_DAY_PAYOUT)');
/**
 * The business access ceiling. Roles can only narrow these grants; they cannot
 * add a product area, action, or payment method absent here.
 */
export const BusinessAccessV2Schema = z.object({
    productAreas: z.array(BusinessProductAreaV2Schema).default([]),
    actions: z.array(BusinessActionEnumSchema).default([]),
    paymentMethods: z.array(BusinessPaymentMethodV2Schema).default([]),
    policy: BusinessAccessPolicyV2Schema,
    settings: BusinessSettingsV2Schema.default({}),
    /**
     * Freeform uppercase snake_case labels used to tag a business for search
     * and filtering (e.g. `NEXT_DAY_PAYOUT`). Not tied to any enum — new
     * tags can be introduced without a code change. Duplicates are rejected.
     */
    tags: z
        .array(BusinessAccessTagV2Schema)
        .max(50)
        .refine((tags) => new Set(tags).size === tags.length, {
        message: 'Tags must be unique',
    })
        .default([]),
});
